import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';

import { MedicoService } from 'src/app/services/medico.service';
import { Especialidad } from 'src/app/models/especialidad.interface';
import { Hospital } from 'src/app/models/hospital.interface';
import { Institucion } from 'src/app/models/institucion.interface';
import { Estado } from 'src/app/models/estado.interface';
import { Catalogo } from 'src/app/models/catalogos.interface';
import { TipoDropdown } from 'src/app/models/tipo-dropdown.interface';
import { MedicoRequest, UsuarioRequest } from 'src/app/models/enfermera-request.interface';

@Component({
  selector: 'app-medico-registrar-nuevo',
  templateUrl: './medico-registrar-nuevo.component.html',
  styleUrls: ['./medico-registrar-nuevo.component.scss'],
  providers: [MessageService]
})
export class MedicoRegistrarNuevoComponent implements OnInit {
  medicoForm: FormGroup;
  especialidades: Especialidad[] = [];
  hospitales: Hospital[] = [];
  puestos: Catalogo[] = [];
  instituciones: TipoDropdown[] = [];
  estados: TipoDropdown[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    private medicoService: MedicoService,
  ) {
    this.medicoForm = this.crearFormulario();
    this.obtenerInformacion();
    this.confiChange();
  }

  ngOnInit(): void {
    // Validaciones adicionales si es necesario
  }

  onSubmit(): void {
    if (this.medicoForm.valid) {
      this.fullMedico();
    } else {
      this.showError('Por favor complete todos los campos requeridos');
      this.markFormGroupTouched(this.medicoForm);
    }
  }

  crearFormulario(): FormGroup {
    return this.fb.group({
      // Datos de usuario - CON VALIDACIONES COMPLETAS
      usuario: ['', [
        Validators.required, 
        Validators.email,
        Validators.minLength(5),
        Validators.maxLength(50)
      ]],
      password: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(18),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,18}$/)
      ]],
      alias: ['', Validators.maxLength(50)],
      nombre: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/)
      ]],
      primerApellido: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/)
      ]],
      segundoApellido: ['', [
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/)
      ]],
      telefonoMovil: ['', [
        Validators.pattern(/^[0-9]{10}$/)
      ]],
      
      // Datos de ubicación - TODOS REQUERIDOS
      fk_estado: ['', Validators.required],
      fk_institucion: ['', Validators.required],
      fk_hospital: ['', Validators.required],
      fk_especialidad: ['', Validators.required],
      fk_puesto: ['', Validators.required],
      fk_residencia: ['', Validators.maxLength(200)],
      
      // Datos de consultorio público
      noConsultorio: ['', [
        Validators.maxLength(20),
        Validators.pattern(/^[0-9a-zA-Z\s-]*$/)
      ]],
      telefonoConsultorio: ['', [
        Validators.pattern(/^[0-9]{10}$/)
      ]],
      
      // Datos de consultorio privado
      hospitalConsultorioPrivado: ['', Validators.maxLength(200)],
      noConsultorioDireccionPrivado: ['', Validators.maxLength(200)],
      telefonoConsultorioPrivado: ['', [
        Validators.pattern(/^[0-9]{10}$/)
      ]],
      
      // Datos de puntos
      fk_puntos: [1, Validators.required],
      puntos: [500, [Validators.required, Validators.min(0)]],
      fk_puntos_seg: [1, Validators.required],
      puntos_seg: [500, [Validators.required, Validators.min(0)]],
      fk_rol: [3]
    });
  }

  obtenerInformacion(): void {
    forkJoin({
      subInstituciones: this.medicoService.getInstituciones(),
      subEstados: this.medicoService.getEstados(),
      subEspecialidades: this.medicoService.getEspecialidades(),
      subPuestos: this.medicoService.getPuestos(),
    }).subscribe({
      next: (resp) => {
        this.fullCatalogoInstituciones(resp.subInstituciones);
        this.fullCatalogoEstados(resp.subEstados);
        this.fullCatalogoEspecialidades(resp.subEspecialidades);
        this.fullCatalogoPuestos(resp.subPuestos);
      },
      error: (err) => { 
        console.log(err);
        this.showError('Error al cargar los catálogos');
      }
    });
  }

  fullMedico(): void {
    const { usuario, password, alias, nombre, primerApellido, segundoApellido, telefonoMovil, fk_rol } = this.medicoForm.value;
    const usuarioRequest: UsuarioRequest = { 
      usuario, 
      password, 
      alias, 
      nombre, 
      primerApellido, 
      segundoApellido, 
      telefonoMovil, 
      fk_rol 
    };

    const { 
      fk_especialidad, 
      fk_hospital, 
      fk_puesto, 
      fk_residencia, 
      noConsultorio, 
      telefonoConsultorio,
      hospitalConsultorioPrivado, 
      noConsultorioDireccionPrivado, 
      telefonoConsultorioPrivado,
      fk_puntos, 
      puntos, 
      fk_puntos_seg, 
      puntos_seg 
    } = this.medicoForm.value;

    const idHospital = fk_hospital?.id || fk_hospital;
    const idEspecialidad = fk_especialidad?.id || fk_especialidad;
    const idPuesto = fk_puesto?.id || fk_puesto;
    const correo = usuario;
    
    const medicoRequest: MedicoRequest = {
      fk_especialidad: idEspecialidad, 
      fk_hospital: idHospital, 
      fk_puesto: idPuesto, 
      correo,
      telefonoMovil, 
      fk_residencia, 
      noConsultorio, 
      telefonoConsultorio, 
      hospitalConsultorioPrivado,
      noConsultorioDireccionPrivado, 
      telefonoConsultorioPrivado, 
      fk_puntos, 
      puntos, 
      fk_puntos_seg, 
      puntos_seg,
      usuario: usuarioRequest
    };
    
    this.setMedico(medicoRequest);
  }

  fullCatalogoEspecialidades(items: Especialidad[]): void {
    this.especialidades = [...items];
  }
  
  fullCatalogoPuestos(items: Catalogo[]): void {
    this.puestos = [...items];
  }
  
  fullCatalogoHospitales(items: Hospital[]): void {
    this.hospitales = [...items];
  }
  
  fullCatalogoInstituciones(instituciones: Institucion[]): void {
    const items: TipoDropdown[] = [...instituciones.map((item: Institucion) => this.institucionToTipoDropdown(item))];
    this.instituciones = [...items];
  }
  
  fullCatalogoEstados(estados: Estado[]): void {
    const items: TipoDropdown[] = [...estados.map((item: Estado) => this.estadoToTipoDropdown(item))];
    this.estados = [...items];
  }
  
  institucionToTipoDropdown(institucion: Institucion): TipoDropdown {
    return { value: `${institucion.id}`, label: institucion.cvInstitucion };
  }
  
  estadoToTipoDropdown(estado: Estado): TipoDropdown {
    return { value: `${estado.id}`, label: estado.nomEstado };
  }
  
  confiChange(): void {
    this.medicoForm.get('fk_estado')?.valueChanges.subscribe((value) => { 
      this.getHospitales(); 
    });
    
    this.medicoForm.get('fk_institucion')?.valueChanges.subscribe((value) => { 
      this.getHospitales(); 
    });
  }
  
  getHospitales(): void {
    const idInstitucion: number = this.medicoForm.get('fk_institucion')?.value;
    const idEstado: number = this.medicoForm.get('fk_estado')?.value;

    if (idInstitucion && idEstado) {
      this.medicoService.getHospitales(idInstitucion, idEstado).subscribe({
        next: (resp) => {
          this.fullCatalogoHospitales(resp);
        },
        error: (err) => { 
          console.log(err);
          this.showError('Error al cargar los hospitales');
        }
      });
    }
  }
  
  setMedico(medico: MedicoRequest): void {
    this.medicoService.nuevoMedico(medico).subscribe({
      next: (resp) => {
        this.showSuccess(resp.mensaje || 'Médico registrado exitosamente');
        this.router.navigate(['/registrar']);
      },
      error: (err: HttpErrorResponse) => { 
        this.showError(err.error?.message || 'Error al registrar el médico'); 
        console.log(err);
      }
    });
  }
  
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
  
  cancelar(): void {
    this.router.navigate(['/registrar']);
  }
  
  showSuccess(mensaje: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: mensaje,
      life: 3000
    });
  }
  
  showError(mensaje: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mensaje,
      life: 5000
    });
  }
  
  get f() {
    return this.medicoForm.controls;
  }
}